import React from 'react';
import {
  required,
  BooleanField,
  BooleanInput,
  Create,
  Datagrid,
  DateField,
  Edit,
  FileField,
  FileInput,
  ImageField,
  ImageInput,
  ReferenceInput,
  SelectField,
  SelectInput,
  Show,
  SimpleForm,
  Tab,
  TabbedShowLayout,
  TextField,
  TextInput
} from 'react-admin';

import List from './List';
import { S3FileField, S3ImageField, JSONField } from './fields';
import { handleRelated } from './utils';

const TemplateTypes = [
  { id: 'standard', name: 'Standard' },
  { id: 'media', name: 'Media' },
  { id: 'bespoke', name: 'Bespoke' }
];

export const TemplateList = props => (
  <List {...props} sort={{ field: 'updated_at', order: 'DESC' }}>
    <Datagrid rowClick="show">
      <TextField source="name" fullWidth />
      <TextField source="version" fullWidth />
      <TextField source="editorDirectory" fullWidth />
      <TextField source="betaEditorDirectory" label="Beta Editor Directory" fullWidth />
      <SelectField source="type" choices={TemplateTypes} fullWidth />
      <BooleanField source="hasHotspots" />
      <TextField
        source="templateFile.name"
        label="Template File"
        sortable={false}
        fullWidth
      />
      <TextField
        source="betaTemplateFile.name"
        label="Beta Template File"
        sortable={false}
        fullWidth
      />
      <DateField source="created_at" showTime />
      <DateField source="updated_at" showTime />
      <BooleanField source="disabled" />
      <BooleanField source="featured" />
    </Datagrid>
  </List>
);

export const TemplateCreate = props => (
  <Create redirect="list" {...props}>
    <SimpleForm width={'50%'}>
      <TextInput fullWidth source="name" validate={[required()]} />
      <TextInput fullWidth source="version" />
      <TextInput
        fullWidth
        className=''
        source="editorDirectory"
        helperText="Absolute path e.g. /unity-editor/"
      />
      <TextInput
        fullWidth
        source="betaEditorDirectory"
        helperText="Beta editor path e.g. /unity-editor-beta/"
      />
      <SelectInput
        source="type"
        choices={TemplateTypes}
        validate={[required()]}
        fullWidth
      />
      <BooleanInput source="hasHotspots" />
      <ImageInput
        source="thumbnail"
        label="Thumbnail"
        accept="image/*"
        validate={[required()]}
      >
        <ImageField source="src" title="title" />
      </ImageInput>
      <ReferenceInput
        label="Template File"
        reference="template-files"
        source="templateFile"
        perPage={100}
      >
        <SelectInput optionText="name" format={handleRelated} />
      </ReferenceInput>
      <ReferenceInput
        label="Beta Template File"
        reference="template-files"
        source="betaTemplateFile"
        perPage={100}
      >
        <SelectInput optionText="name" format={handleRelated} />
      </ReferenceInput>
      <FileInput source="file" label="File">
        <FileField source="src" title="title" />
      </FileInput>
      <FileInput source="betaFile" label="Beta Template File Direct">
        <FileField source="src" title="title" />
      </FileInput>
      <FileInput
        source="defaultConfig"
        label="Default Config JSON"
        accept="application/json"
        validate={[required()]}
      >
        <FileField source="src" title="title" />
      </FileInput>
      <FileInput
        source="betaDefaultConfig"
        label="Beta Default Config JSON"
        accept="application/json"
      >
        <FileField source="src" title="title" />
      </FileInput>
      <BooleanInput source="disabled" />
      <BooleanInput source="featured" />
    </SimpleForm>
  </Create>
);

export const TemplateShow = props => (
  <Show {...props}>
    <TabbedShowLayout>
      <Tab label="Summary">
        <TextField source="name" fullWidth />
        <TextField source="version" fullWidth />
        <TextField source="editorDirectory" fullWidth />
        <TextField source="betaEditorDirectory" label="Beta Editor Directory" fullWidth />
        <SelectField source="type" choices={TemplateTypes} fullWidth />
        <BooleanField source="hasHotspots" />
        <S3ImageField source="thumbnail" />
        <TextField source="templateFile.name" label="Template File" fullWidth />
        <TextField source="betaTemplateFile.name" label="Beta Template File" fullWidth />
        <S3FileField source="file" />
        <S3FileField source="betaFile" label="Beta Template File Direct" />
        <BooleanField source="disabled" />
        <BooleanField source="featured" />
      </Tab>
      <Tab label="Default Config">
        <JSONField source="defaultConfig" />
      </Tab>
      <Tab label="Beta Config">
        <JSONField source="betaDefaultConfig" />
      </Tab>
    </TabbedShowLayout>
  </Show>
);

export const TemplateEdit = props => (
  <Edit redirect="show" {...props}>
    <SimpleForm width={'50%'}>
      <TextInput source="name" validate={[required()]} fullWidth />
      <TextInput source="version" fullWidth />
      <TextInput
        source="editorDirectory"
        helperText="Absolute path e.g. /unity-editor/"
        fullWidth
      />
      <TextInput
        source="betaEditorDirectory"
        helperText="Beta editor path e.g. /unity-editor-beta/"
        fullWidth
      />
      <SelectInput
        source="type"
        choices={TemplateTypes}
        validate={[required()]}
        fullWidth
      />
      <BooleanInput source="hasHotspots" />
      <ImageInput
        source="thumbnail"
        label="Thumbnail"
        accept="image/*"
        validate={[required()]}
      >
        <ImageField source="src" title="title" />
      </ImageInput>
      <ReferenceInput
        label="Template File"
        reference="template-files"
        source="templateFile"
        perPage={100}
      >
        <SelectInput optionText="name" format={handleRelated} />
      </ReferenceInput>
      <ReferenceInput
        label="Beta Template File"
        reference="template-files"
        source="betaTemplateFile"
        perPage={100}
      >
        <SelectInput optionText="name" format={handleRelated} />
      </ReferenceInput>
      <FileInput source="file" label="Template File Direct">
        <FileField source="src" title="title" />
      </FileInput>
      <FileInput source="betaFile" label="Beta Template File Direct">
        <FileField source="src" title="title" />
      </FileInput>
      <FileInput
        source="defaultConfig"
        label="Default Config JSON"
        accept="application/json"
        validate={[required()]}
      >
        <FileField source="src" title="title" />
      </FileInput>
      <FileInput
        source="betaDefaultConfig"
        label="Beta Default Config JSON"
        accept="application/json"
      >
        <FileField source="src" title="title" />
      </FileInput>
      <BooleanInput source="disabled" />
      <BooleanInput source="featured" />
    </SimpleForm>
  </Edit>
);
